import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, Mail, MapPin, Loader2, Phone, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import PageSEO from '../components/PageSEO';
import StructuredData from '../components/StructuredData';

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('contact.formError'));
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post('/contact.php', formData);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', message: '' });
      } else {
        toast.error(response.data.message || t('contact.formFail'));
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.formFail'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative pt-20" data-testid="contact-page">
      <PageSEO path="/contact" />
      <StructuredData path="/contact" />
      <div className="ambient-glow ambient-orange" />
      <div className="ambient-glow ambient-blue" />
      <div className="fixed top-20 left-0 right-0 glow-line z-40" />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('contact.heroLabel')}</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
              {t('contact.heroTitle1')}
              <span className="gradient-text-orange">{t('contact.heroTitle2')}</span>
            </h1>
            <p className="text-zinc-400 max-w-3xl mx-auto text-base md:text-lg">{t('contact.heroDesc')}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-[#05050A]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8">
              <h2 className="font-heading text-2xl font-bold text-white mb-6">{t('contact.formTitle')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-zinc-400 mb-2">{t('contact.formName')}</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder={t('contact.formNamePlaceholder')} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#FF5E00] focus:ring-1 focus:ring-[#FF5E00]/50 transition-all" data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-zinc-400 mb-2">{t('contact.formEmail')}</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('contact.formEmailPlaceholder')} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#FF5E00] focus:ring-1 focus:ring-[#FF5E00]/50 transition-all" data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-zinc-400 mb-2">{t('contact.formMessage')}</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder={t('contact.formMessagePlaceholder')} rows={6} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#FF5E00] focus:ring-1 focus:ring-[#FF5E00]/50 transition-all resize-none" data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={isSubmitting} className="glow-button w-full px-6 py-4 text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-button">
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />{t('contact.formSending')}</>
                  ) : (
                    <><Send className="w-5 h-5" />{t('contact.formSend')}</>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <a href="tel:+35799580396" className="glass-card rounded-2xl p-6 block group hover:border-[#FF5E00]/50 transition-all" data-testid="contact-phone-cyprus">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-[#FF5E00]/20 flex items-center justify-center group-hover:bg-[#FF5E00]/30 transition-colors">
                    <Phone className="w-8 h-8 text-[#FF5E00]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl">{t('contact.cyprus')}</h3>
                    <p className="text-[#FF5E00] text-sm">+357 99 580 396</p>
                  </div>
                </div>
              </a>

              <a href="tel:+306951976619" className="glass-card rounded-2xl p-6 block group hover:border-[#00D4FF]/50 transition-all" data-testid="contact-phone-greece">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-[#00D4FF]/20 flex items-center justify-center group-hover:bg-[#00D4FF]/30 transition-colors">
                    <Phone className="w-8 h-8 text-[#00D4FF]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl">{t('contact.greece')}</h3>
                    <p className="text-[#00D4FF] text-sm">+30 695 197 6619</p>
                  </div>
                </div>
              </a>

              <a href="mailto:Info@rteqsolutions.com" className="glass-card rounded-2xl p-6 block group hover:border-[#FF5E00]/50 transition-all" data-testid="contact-email-card">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-[#FF5E00]/20 flex items-center justify-center group-hover:bg-[#FF5E00]/30 transition-colors">
                    <Mail className="w-8 h-8 text-[#FF5E00]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl">{t('contact.email')}</h3>
                    <p className="text-[#FF5E00] text-sm">Info@rteqsolutions.com</p>
                  </div>
                </div>
              </a>

              <div className="glass-card rounded-2xl p-6" data-testid="contact-location-card">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-[#00D4FF]/20 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-[#00D4FF]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl">{t('contact.address')}</h3>
                    <p className="text-zinc-400 text-sm">Galilaiou 11, Agios Ioannis</p>
                    <p className="text-zinc-400 text-sm">3015 Limassol, Cyprus</p>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-[#FF5E00]/20 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-[#FF5E00]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl">{t('contact.businessHours')}</h3>
                    <p className="text-zinc-400 text-sm">{t('contact.monFri')}</p>
                    <p className="text-[#FF5E00] text-sm font-medium">07:30 - 17:00</p>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6">
                <h3 className="font-heading font-bold text-white text-xl mb-3">{t('contact.ctaTitle')}</h3>
                <p className="text-zinc-400 text-sm mb-4">{t('contact.ctaDesc')}</p>
                <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button w-full px-6 py-3 text-sm" data-testid="contact-get-started-button">{t('contact.ctaBtn')}</a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t('contact.bottomTitle')}</h3>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">{t('contact.bottomDesc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/services" className="glow-button px-8 py-4 text-base">{t('contact.bottomBtn')}</Link>
              <Link to="/suite" className="secondary-button px-8 py-4 text-base">{t('contact.bottomBtn2')}</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
